import { ThemeEvents } from "@theme/events";
import { prefersReducedMotion } from "@theme/utilities";

class PageTransition extends HTMLElement {
  constructor() {
    super();

    window.addEventListener("beforeunload", () => {
      document.body.classList.add("page-loading");
      document.dispatchEvent(
        new CustomEvent(ThemeEvents.pageTransitionStart, {
          bubbles: true,
          detail: { direction: "out", timestamp: Date.now() },
        })
      );
    });

    // Safari 15: Module scripts may load after DOMContentLoaded has fired
    // Check readyState and run immediately if DOM is already ready
    const initOnLoad = () => {
      document.dispatchEvent(
        new CustomEvent(ThemeEvents.pageLoaded, {
          bubbles: true,
          detail: { timestamp: Date.now() },
        })
      );
      this.hide();
    };

    if (document.readyState === "loading") {
      // DOM is still loading, wait for DOMContentLoaded
      window.addEventListener("DOMContentLoaded", initOnLoad, { once: true });
    } else {
      // DOM is already ready (interactive or complete), run immediately
      initOnLoad();
    }

    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        document.body.classList.remove("page-loading");
      }
    });
  }

  hide() {
    const finish = () => {
      if (this.hidden) return;
      this.hidden = true;
      document.dispatchEvent(
        new CustomEvent(ThemeEvents.pageTransitionEnd, {
          bubbles: true,
          detail: { timestamp: Date.now() },
        })
      );
    };

    // Fade out by toggling a class instead of animating opacity via the Web
    // Animations API. The class (and the opacity change it drives) is a real
    // DOM mutation that session replay can record, so replays match what
    // shoppers actually see; WAAPI-driven opacity changes are invisible to
    // rrweb, which is what made recordings disagree with real page loads.
    this.classList.add("page-transition--hidden");

    // Matches the `transition: opacity 0.15s` in critical-css.liquid.
    const duration = prefersReducedMotion() ? 0 : 150;
    // Finalize when the CSS transition ends, with a timeout backstop in case
    // transitionend never fires (e.g. reduced motion, or no perceptible change).
    this.addEventListener("transitionend", finish, { once: true });
    setTimeout(finish, duration + 50);
  }
}

if (!customElements.get("page-transition")) {
  customElements.define("page-transition", PageTransition);
}
